import {DrinkActivitySummary, DrinkOption, DrinkActivityOptions, fromTimestamp} from "../drinkService";
import {Drink, DrinkActivity} from "../../generated/drinks_pb";
import {AuthUser} from "../../context/AuthContext";
import {DrinkServiceLolPromiseClient} from "../../generated/drinks_grpc_web_pb";
import GRPC_BACKEND_URL, { GRPC_CLIENT_OPTIONS } from "../../grpcConfig";
import {buildUserMessage} from "./utils/user";
import {toTimestamp} from "./utils/timestamp";
export const drinkClient = new DrinkServiceLolPromiseClient(GRPC_BACKEND_URL, null, GRPC_CLIENT_OPTIONS);


export const fetchAvailableDrinks = async (): Promise<DrinkOption[]> => {
    const request = new Drink();
    const response = await drinkClient.getDrinks(request, {});
    return response.getDrinksList().map((drink) => ({
        id: drink.getDrinkid(),
        name: drink.getDrinkname(),
        promille: drink.getPromille(),
    }));
};

export const fetchDrinkActivities = async (user: AuthUser): Promise<DrinkActivitySummary[]> => {
    const response = await drinkClient.getActivities(buildUserMessage(user), {});
    return response.getDrinkingactivitesList().map((activity) => {
        const drink = activity.getDrink();
        const finishedAt = fromTimestamp(activity.getTsinsert());
        const startedAt = fromTimestamp(activity.getTsstart());
        const durationMinutes =
            finishedAt && startedAt
                ? Math.max(0, Math.round((finishedAt.getTime() - startedAt.getTime()) / (60 * 1000)))
                : null;
        return {
            activityId: activity.hasActivityid() ? activity.getActivityid() : undefined,
            amountMl: activity.getAmount(),
            drinkId: drink ? drink.getDrinkid() : null,
            drinkName: drink?.getDrinkname() ?? "Unknown",
            promille: drink?.getPromille() ?? 0,
            timestamp: finishedAt,
            eventId: activity.hasEventid() ? activity.getEventid() : null,
            startedAt,
            durationMinutes,
        };
    });
};

const buildDrinkActivity = (
    user: AuthUser,
    drink: DrinkOption,
    amountMl: number,
    consumedAt: Date,
    options?: DrinkActivityOptions
): DrinkActivity => {
    const activity = new DrinkActivity();
    const drinkMessage = new Drink();
    drinkMessage.setDrinkname(drink.name);
    drinkMessage.setPromille(drink.promille);
    drinkMessage.setDrinkid(drink.id);
    activity.setDrink(drinkMessage);
    activity.setUser(buildUserMessage(user));
    activity.setAmount(amountMl);
    activity.setTsinsert(toTimestamp(consumedAt));

    const durationMinutes = options?.durationMinutes ?? null;
    const startedAt =
        options?.startedAt ??
        (durationMinutes != null
            ? new Date(consumedAt.getTime() - durationMinutes * 60 * 1000)
            : null);

    if (startedAt) {
        activity.setTsstart(toTimestamp(startedAt));
    } else {
        activity.clearTsstart();
    }
    if (options?.eventId && options.eventId > 0) {
        activity.setEventid(options.eventId);
    }

    return activity;
};

export const addDrinkActivity = async (
    user: AuthUser,
    drink: DrinkOption,
    amountMl: number,
    consumedAt: Date,
    options?: DrinkActivityOptions
): Promise<void> => {
    const metadata: Record<string, string> = options?.idempotencyKey ? { "idempotency-key": options.idempotencyKey } : {};
    await drinkClient.addDrinkingEvent(buildDrinkActivity(user, drink, amountMl, consumedAt, options), metadata);
};

export const updateDrinkActivity = async (
    user: AuthUser,
    activityId: number,
    drink: DrinkOption,
    amountMl: number,
    consumedAt: Date,
    options?: DrinkActivityOptions
): Promise<void> => {
    const activity = buildDrinkActivity(user, drink, amountMl, consumedAt, options);
    activity.setActivityid(activityId);

    await drinkClient.updateDrinkingEvent(activity, {});
};

export const deleteDrinkActivity = async (user: AuthUser, activityId: number): Promise<void> => {
    const activity = new DrinkActivity();
    activity.setActivityid(activityId);
    activity.setUser(buildUserMessage(user));
    await drinkClient.deleteDrinkingEvent(activity, {});
};
